/* The booking link the lobby screen sends somebody to, and when it does.
 *
 * A salon with an hour's wait on a Saturday does not want that person on a
 * waiting list. They want them booked, on a day the salon can actually take
 * them — so past some length of wait the tablet stops offering a queue and
 * offers a time instead. Which length is the salon's decision (`cleanCutoff`),
 * and where it sends them is a link they paste in (`cleanBookingUrl`).
 *
 * The link is a TEMPLATE rather than an address. A booking site can deep-link
 * to one service — `?serviceId=1234`, or `/book/1234` — and the whole value of
 * this over a poster by the door is that somebody who tapped Pedicure lands on
 * Pedicure. So the salon writes the id's place as `{service}` and each service
 * carries the id that goes in it; see `bookingUrl`.
 *
 * ── WHY THIS IS A RULES FILE ─────────────────────────────────────────────────
 *
 * Two of the three things here end up in front of a client on a screen nobody is
 * standing at, and one of them ends up in an `href`:
 *
 *   the cutoff   decides whether a person is offered a queue or turned away to
 *                book. Off by a factor of sixty and a salon sends everybody
 *                away all day, or nobody ever.
 *   the link     is typed by hand into a settings box and rendered as a link on
 *                a tablet. `http(s)` is enforced here, once, so no amount of
 *                what somebody pastes into that box can put a `javascript:` URL
 *                on a lobby screen.
 *   the template is string surgery, and the awkward half is the service with NO
 *                id: dropping `{service}` out of `?serviceId={service}` has to
 *                leave a link that still opens the booking site rather than one
 *                with a dangling empty parameter.
 *
 * All three are assertable without a DOM or a Supabase client, which is the same
 * reason js/rules/waitlist.js and js/rules/checkin-look.js exist. This file
 * imports nothing.
 */

// The cutoff, in minutes of estimated wait. The default is an hour because that
// is the point at which "wait here" stops being a favour — under it people
// generally would rather stay, and a salon that disagrees moves it.
export var CUTOFF_DEFAULT_MIN = 60;

/* What a salon may set it to. The floor is not politeness: a cutoff of a minute
 * or two would send away everybody the board could have seated in the time it
 * takes to read the screen, and the waiting list would effectively be gone
 * without anybody having switched it off. The ceiling is a whole working day,
 * past which the setting has no meaning — nothing is ever quoted that far out. */
export var CUTOFF_LIMITS = { min: 5, max: 480 };

// A URL somebody pastes, and a service id somebody types. Both are column
// widths rather than judgements; the id in particular is a number at Mangomint
// and a slug elsewhere, so nothing here has an opinion about its shape.
export var URL_MAX = 300;
export var REF_MAX = 64;

// What the settings screen tells people to type. One canonical spelling, but
// `{serviceId}`, `{service_id}` and `{ service }` all work — see `token`: this
// is read off a hint by somebody in a hurry, and refusing the near-miss would
// mean a link that silently sends every service to the same page.
export var PLACEHOLDER = '{service}';

/* Fresh each call, deliberately. A `/g` regex carries `lastIndex` between
 * calls, so a shared one would have `test` and `replace` interfering with each
 * other — `hasPlaceholder` answering false every second call is exactly the bug
 * that reads as "the placeholder works sometimes". */
function token() { return /\{\s*service[ _-]?(?:id)?\s*\}/gi; }

/* The shape a link has to have, and the only parser here.
 *
 * Not `new URL`, and that is not stylistic. The WHATWG parser percent-encodes
 * `{` and `}` in a PATH, so a perfectly good `https://book.example.com/svc/{service}`
 * comes back as `.../svc/%7Bservice%7D` and the placeholder can never be found
 * again. Splitting it by hand leaves the template exactly as the salon typed it.
 */
var URL_SHAPE = /^(https?:\/\/)([^\s/?#]+)([^\s?#]*)(\?[^\s#]*)?(#\S*)?$/i;

// Enough of a hostname to believe somebody meant a web address, for deciding
// whether a scheme-less paste gets `https://` put in front of it. Deliberately
// strict: it is what keeps `javascript:...` out of the "they just forgot the
// https" branch, because nothing of that shape has a dotted host on the front.
var HOSTISH = /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(?=$|[:/?#])/i;

/* A link, or '' for anything that is not one.
 *
 * '' is load-bearing in the same way `cleanColor`'s null is: it is what "no
 * booking link" means everywhere downstream, and no booking link is what keeps
 * the cutoff inert. A salon that has not set one cannot be sent away to nowhere.
 *
 * A paste with no scheme gets `https://`, because "booking.mangomint.com/salon"
 * is what a booking site's own address bar shows and typing it is not a mistake.
 * A paste with a scheme keeps it only if it is http or https — everything else,
 * `javascript:` above all, is not a link and comes back ''.
 */
export function cleanBookingUrl(value) {
  if (value == null) return '';
  var v = String(value).trim().slice(0, URL_MAX);
  if (!v) return '';
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) {
    if (!HOSTISH.test(v)) return '';
    v = 'https://' + v;
  }
  var m = URL_SHAPE.exec(v);
  if (!m) return '';
  // The scheme and host lowercased and the rest left alone: a path and a query
  // are case-sensitive to the site being linked to, and a booking id is not
  // ours to change.
  return m[1].toLowerCase() + m[2].toLowerCase() + (m[3] || '') + (m[4] || '') + (m[5] || '');
}

// Whether this link is per-service at all. A salon may perfectly well paste a
// plain booking page with no placeholder in it — that is the static link, every
// service goes to the same place, and nothing below has to be filled in.
export function hasPlaceholder(url) {
  return token().test(String(url == null ? '' : url));
}

// The booking system's id for one service, as typed. Nothing is filtered out
// beyond whitespace and the cap: it is encoded when it goes into the link, so a
// paste with a slash or a space in it produces a wrong link rather than a
// broken one, and wrong is visible in the example line on the settings screen.
export function cleanServiceRef(value) {
  if (value == null) return '';
  return String(value).replace(/\s+/g, '').slice(0, REF_MAX);
}

/* The address for one service: the salon's template with that service's id in
 * it, or '' when there is nowhere to send anybody.
 *
 * Three cases, and the third is the one worth the tests:
 *
 *   no placeholder   a static link. Every service goes to the same page, which
 *                    is a legitimate way to set this up and the only one
 *                    available to a salon with no booking integration.
 *   an id            substituted, and encoded: an id is somebody's typing, and
 *                    a stray `?` in it must not be able to turn the rest of the
 *                    salon's own query string into part of the id.
 *   no id            the placeholder comes OUT, and what it leaves behind is
 *                    tidied — see `tidy`. A service nobody has mapped still has
 *                    to reach the booking site, because the alternative is a
 *                    client sent to a 404 by a settings box somebody has not
 *                    finished filling in. Landing on the front page of the
 *                    booking site is a worse link and a working one.
 */
export function bookingUrl(template, ref) {
  var url = cleanBookingUrl(template);
  if (!url) return '';
  if (!hasPlaceholder(url)) return url;
  var id = cleanServiceRef(ref);
  if (id) return url.replace(token(), encodeURIComponent(id));
  return tidy(url.replace(token(), ''));
}

/* What is left after a placeholder was taken out, made into a link again.
 *
 * `?serviceId=` and a path ending in `/` are both what removal leaves, and both
 * are the kind of thing a booking site answers with an error page rather than
 * its front page — which would put the one screen with nobody standing at it in
 * front of an error. So an emptied parameter is dropped and a trailing slash
 * past the root goes with it.
 */
function tidy(url) {
  var m = URL_SHAPE.exec(url);
  if (!m) return url;
  var path = m[3] || '';
  var query = m[4] || '';
  var hash = m[5] || '';
  if (path.length > 1 && path.charAt(path.length - 1) === '/') path = path.slice(0, -1);
  if (query) {
    var kept = query.slice(1).split('&').filter(function (p) {
      if (!p) return false;
      var eq = p.indexOf('=');
      // A parameter with no `=` at all was never carrying the id — a flag the
      // site wants keeps its place.
      return eq === -1 || p.slice(eq + 1) !== '';
    });
    query = kept.length ? '?' + kept.join('&') : '';
  }
  return m[1] + m[2] + path + query + hash;
}

/* The cutoff, in minutes, or null for "there isn't one".
 *
 * Null is a real answer and the reason this is not a plain number: a salon can
 * have a booking link set up — on the tile notes, in the wording — and still
 * want everybody offered the waiting list however long the wait is. Nothing
 * distinguishes that from "not configured" except being able to store the
 * absence, so an empty box, a zero and a nonsense value all come back null
 * rather than snapping to the default.
 */
export function cleanCutoff(value) {
  if (value == null || value === '' || value === false) return null;
  var n = Math.round(Number(value));
  if (!isFinite(n) || n <= 0) return null;
  return Math.min(CUTOFF_LIMITS.max, Math.max(CUTOFF_LIMITS.min, n));
}
